import { useState } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts, blogCategories } from '../../data/blog';
import { WHATSAPP_CTA_URL } from '../../data/whatsapp';
import SEOMeta from '../../components/SEOMeta';
import ThemeToggle from '../../components/ThemeToggle';
import { useDarkMode } from '../../hooks/useDarkMode';

function BlogHeader() {
  const isDark = useDarkMode();
  return (
    <header
      style={{ background: 'var(--warm-white)', borderBottom: '1px solid var(--beige-mid)' }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-24 flex items-center justify-between">
        <Link to="/">
          <img src={isDark ? '/logo-dark.png' : '/logo-light.png'} alt="Inspira Músicas" className="w-auto object-contain" style={{ height: 52 }} />
        </Link>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <a
            href={WHATSAPP_CTA_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200"
            style={{
              background: 'var(--gold)',
              color: '#fff',
              fontFamily: "'DM Sans', sans-serif",
              boxShadow: '0 2px 12px rgba(200,131,42,0.25)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--gold-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--gold)'; }}
          >
            Criar Minha Música
          </a>
        </div>
      </div>
    </header>
  );
}

function CategoryChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
      style={{
        background: active ? 'var(--gold)' : 'transparent',
        color: active ? '#fff' : 'var(--text-muted)',
        border: `1.5px solid ${active ? 'var(--gold)' : 'rgba(200,131,42,0.25)'}`,
        fontFamily: "'DM Sans', sans-serif",
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.borderColor = 'var(--gold)';
          e.currentTarget.style.color = 'var(--gold)';
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.borderColor = 'rgba(200,131,42,0.25)';
          e.currentTarget.style.color = 'var(--text-muted)';
        }
      }}
    >
      {label}
    </button>
  );
}

function PostCard({ post }: { post: typeof blogPosts[0] }) {
  const [hovered, setHovered] = useState(false);

  return (
    <Link
      to={`/blog/${post.slug}`}
      className="block rounded-2xl overflow-hidden transition-all duration-300"
      style={{
        background: 'var(--warm-white)',
        border: '1px solid var(--border-soft)',
        boxShadow: hovered ? '0 12px 40px rgba(200,131,42,0.14)' : '0 2px 12px rgba(0,0,0,0.04)',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
        textDecoration: 'none',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Cover */}
      <div
        className="flex items-center justify-center"
        style={{
          height: 200,
          background: post.coverColor,
          fontSize: 64,
          transition: 'transform 0.3s ease',
          transform: hovered ? 'scale(1.04)' : 'scale(1)',
        }}
      >
        {post.coverEmoji}
      </div>

      {/* Content */}
      <div style={{ padding: '24px 28px 28px' }}>
        {/* Meta */}
        <div className="flex items-center gap-4 mb-3 flex-wrap">
          <span
            className="text-xs font-bold uppercase tracking-widest"
            style={{ color: 'var(--gold)', fontFamily: "'DM Sans', sans-serif" }}
          >
            {post.category}
          </span>
          <span className="text-xs" style={{ color: 'var(--text-soft)', fontFamily: "'DM Sans', sans-serif" }}>
            {post.date}
          </span>
          <span className="text-xs" style={{ color: 'var(--text-soft)', fontFamily: "'DM Sans', sans-serif" }}>
            {post.readTime} min de leitura
          </span>
        </div>

        {/* Title */}
        <h2
          className="font-bold mb-3 leading-snug"
          style={{
            fontSize: 18,
            color: hovered ? 'var(--gold)' : 'var(--heading-ink)',
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 800,
            letterSpacing: '-0.02em',
            transition: 'color 0.2s',
          }}
        >
          {post.title}
        </h2>

        {/* Excerpt */}
        <p
          className="text-sm leading-relaxed mb-5"
          style={{
            color: 'var(--text-strong)',
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 300,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          } as React.CSSProperties}
        >
          {post.excerpt}
        </p>

        {/* CTA */}
        <div
          className="flex items-center gap-1.5 text-sm font-semibold"
          style={{ color: 'var(--gold)', fontFamily: "'DM Sans', sans-serif" }}
        >
          Ler artigo
          <span
            style={{
              display: 'inline-block',
              transition: 'transform 0.2s',
              transform: hovered ? 'translateX(4px)' : 'translateX(0)',
            }}
          >
            →
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState('Todos');

  const filtered = activeCategory === 'Todos'
    ? blogPosts
    : blogPosts.filter((p) => p.category === activeCategory);

  return (
    <>
      <SEOMeta
        title="Blog | Inspira Músicas"
        description="Dicas, histórias e inspiração sobre músicas personalizadas. Presentes únicos feitos com inteligência artificial."
        path="/blog"
        ogType="website"
      />
    <div style={{ background: 'var(--cream)', minHeight: '100vh' }}>
      <BlogHeader />

      {/* Hero */}
      <section style={{ background: 'var(--warm-white)', borderBottom: '1px solid var(--beige-mid)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20 text-center">
          <span
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold mb-6"
            style={{
              background: 'var(--badge-bg)',
              color: 'var(--gold-hover)',
              fontFamily: "'DM Sans', sans-serif",
              border: '1px solid rgba(200,131,42,0.15)',
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--gold)' }} />
            Blog Inspira Músicas
          </span>
          <h1
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{ color: 'var(--heading-ink)', fontFamily: "'DM Sans', sans-serif", fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.15 }}
          >
            Histórias, Emoções e{' '}
            <em className="italic" style={{ color: 'var(--gold)' }}>Melodias</em>
          </h1>
          <p
            className="text-base md:text-lg max-w-xl mx-auto"
            style={{ color: 'var(--text-strong)', fontFamily: "'DM Sans', sans-serif", fontWeight: 300, lineHeight: 1.7 }}
          >
            Dicas, histórias reais e tudo sobre o poder de transformar sentimentos em canções.
          </p>
        </div>
      </section>

      {/* Filter + Grid */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        {/* Category filter */}
        <div className="flex gap-2 flex-wrap mb-10">
          {blogCategories.map((cat) => (
            <CategoryChip
              key={cat}
              label={cat}
              active={activeCategory === cat}
              onClick={() => setActiveCategory(cat)}
            />
          ))}
        </div>

        {/* Grid */}
        {filtered.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontFamily: "'DM Sans', sans-serif" }}>
            Nenhum post nessa categoria ainda.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((post) => (
              <PostCard key={post.slug} post={post} />
            ))}
          </div>
        )}
      </section>

      {/* Bottom CTA banner */}
      <section style={{ background: 'var(--warm-white)', borderTop: '1px solid var(--beige-mid)' }}>
        <div className="max-w-2xl mx-auto px-4 md:px-8 py-14 text-center">
          <p
            className="text-2xl md:text-3xl font-bold mb-4"
            style={{ color: 'var(--heading-ink)', fontFamily: "'DM Sans', sans-serif", fontWeight: 800, letterSpacing: '-0.03em' }}
          >
            Pronto para criar a sua música?
          </p>
          <p
            className="text-base mb-7"
            style={{ color: 'var(--text-strong)', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
          >
            Entrega em até 30 minutos. Garantia de satisfação ou dinheiro de volta.
          </p>
          <a
            href={WHATSAPP_CTA_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-8 py-3.5 rounded-full font-semibold text-base transition-all duration-200"
            style={{
              background: 'var(--gold)',
              color: '#fff',
              fontFamily: "'DM Sans', sans-serif",
              boxShadow: '0 4px 20px rgba(200,131,42,0.30)',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--gold-hover)';
              e.currentTarget.style.boxShadow = '0 6px 28px rgba(200,131,42,0.40)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--gold)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(200,131,42,0.30)';
            }}
          >
            Criar Minha Música Agora
          </a>
        </div>
      </section>
    </div>
    </>
  );
}
